<template>
  <b-container class="grade-modal-form">

    <b-row align-v="end"
           class="m-0 p-0">

      <b-col v-if="isProposalOfInfrastructureType(proposal, 'synchrotron')"
             class="pt-2 mx-0 px-0"
             cols="2">

        <label class="mt-2 font-weight-bold" for="rapid-access-acceptance">Proposal status</label>
        <div>
          <traffic_lights_buttons id="rapid-access-acceptance"
                                  v-model="proposal.final_grade.acceptance_mark"
                                  :disabled="disabled"
                                  :options="trafficLightsOptions"
                                  stacked/>
        </div>

      </b-col>
    </b-row>

    <b-form-group label="Comment for User (also visible for Solaris management and beamline staff)"
                  label-class="mt-2 font-weight-bold"
                  label-for="comment">
      <b-form-select :disabled="disabled"
                     :options="review_comments"
                     class="col-md-6"
                     @input="use_comment($event)"/>
      <b-form-textarea id="comment"
                       v-model="proposal.final_grade.comments"
                       :readonly="disabled"
                       rows="3"/>
    </b-form-group>

    <b-form-group label="Comment for Solaris management and beamline staff"
                  label-class="mt-2 font-weight-bold"
                  label-for="comments_for_management">
      <b-form-textarea id="comments_for_management"
                       v-model="proposal.final_grade.comments_for_management"
                       :readonly="disabled"
                       rows="3"/>
    </b-form-group>

    <div v-if="areReviewsAvailable">
      <b-table :current-page="currentPage"
               :fields="formFields"
               :items="show_reviews(reviews)"
               :per-page="perPage"
               class="table-responsive"
               head-variant="light"
               hover
               responsive
               striped/>

      <b-button class="ma-2 right"
                variant="success"
                @click="show_deleted">
        {{ hide_deleted ? 'Show' : 'Hide' }} deleted reviews
      </b-button>

      <b-button v-if="isEditable"
                class="ma-2 right"
                variant="info"
                @click="save_final_grade">
        SAVE
      </b-button>

      <b-pagination v-model="currentPage"
                    :per-page="perPage"
                    :total-rows="show_reviews(reviews).length"
                    size="md"/>
    </div>

    <div v-else>
      <label class="center">This proposal has no review yet</label>
    </div>

  </b-container>
</template>

<script>
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import { comments } from '@/json/reviewComments';
  import trafficLightsOptions from '@/json/trafficLightsAcceptanceMark';
  import traffic_lights_buttons from '@/components/traffic_lights_buttons';

  export default {
    name: 'RapidAccessFinalGradeForm',
    components: { traffic_lights_buttons },
    props: {
      value: {
        type: Object,
        default: () => {
        },
      },
      isEditable: {
        type: Boolean,
        required: true,
      },
    },
    mixins: [infrastructureMixin],
    data() {
      return {
        proposal: JSON.parse(JSON.stringify(this.value)),
        reviews: {},
        hide_deleted: true,
        review_comments: comments,
        perPage: 5,
        currentPage: 1,
        fg_states: '',
        fg_comments: '',
        specified_environment: '',
        formFields: [],
      };
    },
    computed: {
      proposalId() {
        return this.getId(this.proposal);
      },
      disabled() {
        if (!this.isEditable) return true;
        if (this.fg_states !== undefined) {
          if (this.fg_states.name !== undefined) {
            return this.fg_states.name !== 'draft';
          }
          return false;
        }
        return false;
      },
      areReviewsAvailable() {
        return Object.keys(this.reviews).length > 0;
      },
      trafficLightsOptions() {
        return [trafficLightsOptions[0], trafficLightsOptions[trafficLightsOptions.length - 1]];
      },
    },
    methods: {
      show_deleted() {
        this.hide_deleted = !this.hide_deleted;
      },
      show_reviews(reviews) {
        if (this.hide_deleted) {
          const response = [];
          reviews.forEach((review) => {
            if (review.states.name !== 'deleted') {
              response.push(review);
            }
          });
          return response;
        }
        return reviews;
      },
      save_final_grade() {
        this.axios.post(
          `/documents/rapid-access/${this.proposalId}/final-grade/${this.infrastructure}`,
          this.proposal.final_grade,
        )
          .then((response) => {
            if (response.data) {
              this.proposal.final_grade = response.data;
              this.$emit('input', this.proposal);
              this.$notify({ type: 'success', title: 'Saved successfully!' });
              this.closeModalEmit();
            } else {
              this.$notify({ type: 'error', title: 'Error during saving' });
            }
          })
          .catch(() => {
            this.$notify({ type: 'error', title: 'Error during saving' });
          });
      },
      load_reviews() {
        this.axios.get(`/documents/rapid-access/${this.proposalId}/review/${this.infrastructure}`)
          .then((response) => {
            this.reviews = response.data;
          })
          .catch(() => {
            this.$notify({ type: 'error', title: 'Could not load reviews' });
          });
      },
      use_comment(option) {
        if (this.fg_comments === '' || this.fg_comments === undefined) {
          this.$set(this.proposal.final_grade, 'comments', option);
        } else {
          let final_comment = this.fg_comments;
          final_comment += `\n${option}`;
          this.fg_comments = final_comment;
        }
      },
      handleAverageAcceptanceMark() {
        if (!this.proposal.final_grade.acceptance_mark) {
          const reviews = this.proposal.reviews ?? [];
          const submittedReviews = reviews.filter((singleReview) => singleReview.states.name === 'submitted');
          const acceptanceMarksTable = submittedReviews.map((singleReview) => singleReview.acceptance_mark);

          this.proposal.final_grade.acceptance_mark = acceptanceMarksTable.length >= 2 && acceptanceMarksTable.every(
            (singleAcceptanceMark) => singleAcceptanceMark === acceptanceMarksTable[0],
          ) ? acceptanceMarksTable[0] : '';
        }
      },
    },
    async created() {
      const helpers = await this.getRapidAccessEvaluationDashboardProperties(this.infrastructure, 'FinalGrade');
      this.formFields = helpers.getSingleField('finalGradeFinalGradesFormFields');
      this.load_reviews();
      this.specified_environment = (
        this.proposal.general.end_station || this.proposal.document_specification.type.toUpperCase()
      );
      this.fg_states = this.proposal.final_grade.states;
      this.fg_comments = this.proposal.final_grade.comments;
      this.handleAverageAcceptanceMark();
    },
  };
</script>

<style lang="scss"
       scoped>
  .pull-right {
    float: right !important;
  }

  .grade-modal-form {
    padding: 0.5rem 0 1rem 0 !important;
    margin: 0.5rem 0 1rem 0 !important;
    max-width: none;
  }
</style>
